type DependencyBucket = Record<string, any>;

export const dependenciesKeys = [
  'buses',
  'storages',
  'authenticators',
  'validators',
  'mappers',
  'repositories',
  'useCases',
  'controllers',
  'eventHandlers',
] as const;

export type DependenciesKeys = (typeof dependenciesKeys)[number];
export type DependeciesKeys = (typeof dependenciesKeys)[number];

type DependencyBuckets = Record<DependeciesKeys, DependencyBucket>;

type EmptyDependencies = {
  [K in DependeciesKeys]: {};
};

type AnyDependencyFactory = (...args: any[]) => DependencyBucket;

type DependencyManagerConfig = Partial<Record<DependeciesKeys, AnyDependencyFactory>>;

type ReplaceBucket<
  TConfig extends DependencyManagerConfig,
  TKey extends DependeciesKeys,
  TFactory extends AnyDependencyFactory,
> = Omit<TConfig, TKey> & {
  [K in TKey]: TFactory;
};

type ResolvedBucket<
  TConfig extends DependencyManagerConfig,
  TKey extends DependeciesKeys,
> = TKey extends keyof TConfig ? ReturnType<NonNullable<TConfig[TKey]>> : {};

type DependencyManagerDependencies<TConfig extends DependencyManagerConfig> = {
  [K in DependeciesKeys]: ResolvedBucket<TConfig, K>;
};

type DependencyManagerContexts<TConfig extends DependencyManagerConfig> = {
  buses: Pick<DependencyManagerDependencies<TConfig>, never>;
  storages: Pick<DependencyManagerDependencies<TConfig>, 'buses'>;
  authenticators: Pick<DependencyManagerDependencies<TConfig>, 'buses' | 'storages'>;
  validators: Pick<DependencyManagerDependencies<TConfig>, 'buses' | 'storages' | 'authenticators'>;
  mappers: Pick<
    DependencyManagerDependencies<TConfig>,
    'buses' | 'storages' | 'authenticators' | 'validators'
  >;
  repositories: Pick<
    DependencyManagerDependencies<TConfig>,
    'buses' | 'storages' | 'authenticators' | 'validators' | 'mappers'
  >;
  useCases: Pick<
    DependencyManagerDependencies<TConfig>,
    'buses' | 'storages' | 'authenticators' | 'validators' | 'mappers' | 'repositories'
  >;
  controllers: Pick<
    DependencyManagerDependencies<TConfig>,
    'buses' | 'storages' | 'authenticators' | 'validators' | 'mappers' | 'repositories' | 'useCases'
  >;
  eventHandlers: Pick<
    DependencyManagerDependencies<TConfig>,
    | 'buses'
    | 'storages'
    | 'authenticators'
    | 'validators'
    | 'mappers'
    | 'repositories'
    | 'useCases'
    | 'controllers'
  >;
};

type DependencyFactoryFor<
  TKey extends DependeciesKeys,
  TConfig extends DependencyManagerConfig,
  TResult extends DependencyBucket,
> = (dependencies: DependencyManagerContexts<TConfig>[TKey]) => TResult;

export type DependenciesSingleton<
  TDependencies extends Partial<DependencyBuckets> = DependencyBuckets,
> = {
  [K in DependeciesKeys]: K extends keyof TDependencies ? TDependencies[K] : DependencyBucket;
};

export type DependenciesEntry<
  TDependencies extends DependenciesSingleton = DependenciesSingleton,
  TResult extends DependencyBucket = DependencyBucket,
> = (dependenciesSingleton: TDependencies) => TResult;

export type DependenciesSetup<TDependencies extends DependenciesSingleton = DependenciesSingleton> =
  {
    [K in DependeciesKeys]: DependenciesEntry<TDependencies>;
  };

export type UnwrapDependencies<T extends Record<PropertyKey, (...args: any[]) => unknown>> = {
  [K in keyof T]: T[K] extends (...args: any[]) => infer R ? R : never;
};

const createEmptyDependencies = (): EmptyDependencies => ({
  buses: {},
  storages: {},
  authenticators: {},
  validators: {},
  mappers: {},
  repositories: {},
  useCases: {},
  controllers: {},
  eventHandlers: {},
});

const emptyFactory = () => ({});

export class DependencyManager<
  TConfig extends DependencyManagerConfig = {},
  _TInitialized extends boolean = false,
> {
  readonly #setup: DependencyManagerConfig;
  readonly #dependencies?: DependencyManagerDependencies<TConfig>;

  constructor(
    setup: DependencyManagerConfig = {},
    dependencies?: DependencyManagerDependencies<TConfig>,
  ) {
    this.#setup = setup;
    this.#dependencies = dependencies;
  }

  private withBucket<
    TKey extends DependeciesKeys,
    TResult extends DependencyBucket,
    TFactory extends DependencyFactoryFor<TKey, TConfig, TResult>,
  >(key: TKey, factory: TFactory): DependencyManager<ReplaceBucket<TConfig, TKey, TFactory>> {
    return new DependencyManager<ReplaceBucket<TConfig, TKey, TFactory>>({
      ...this.#setup,
      [key]: factory,
    });
  }

  buses<TResult extends DependencyBucket>(
    factory: DependencyFactoryFor<'buses', TConfig, TResult>,
  ): DependencyManager<ReplaceBucket<TConfig, 'buses', typeof factory>> {
    return this.withBucket('buses', factory);
  }

  storages<TResult extends DependencyBucket>(
    factory: DependencyFactoryFor<'storages', TConfig, TResult>,
  ): DependencyManager<ReplaceBucket<TConfig, 'storages', typeof factory>> {
    return this.withBucket('storages', factory);
  }

  authenticators<TResult extends DependencyBucket>(
    factory: DependencyFactoryFor<'authenticators', TConfig, TResult>,
  ): DependencyManager<ReplaceBucket<TConfig, 'authenticators', typeof factory>> {
    return this.withBucket('authenticators', factory);
  }

  validators<TResult extends DependencyBucket>(
    factory: DependencyFactoryFor<'validators', TConfig, TResult>,
  ): DependencyManager<ReplaceBucket<TConfig, 'validators', typeof factory>> {
    return this.withBucket('validators', factory);
  }

  mappers<TResult extends DependencyBucket>(
    factory: DependencyFactoryFor<'mappers', TConfig, TResult>,
  ): DependencyManager<ReplaceBucket<TConfig, 'mappers', typeof factory>> {
    return this.withBucket('mappers', factory);
  }

  repositories<TResult extends DependencyBucket>(
    factory: DependencyFactoryFor<'repositories', TConfig, TResult>,
  ): DependencyManager<ReplaceBucket<TConfig, 'repositories', typeof factory>> {
    return this.withBucket('repositories', factory);
  }

  useCases<TResult extends DependencyBucket>(
    factory: DependencyFactoryFor<'useCases', TConfig, TResult>,
  ): DependencyManager<ReplaceBucket<TConfig, 'useCases', typeof factory>> {
    return this.withBucket('useCases', factory);
  }

  controllers<TResult extends DependencyBucket>(
    factory: DependencyFactoryFor<'controllers', TConfig, TResult>,
  ): DependencyManager<ReplaceBucket<TConfig, 'controllers', typeof factory>> {
    return this.withBucket('controllers', factory);
  }

  controller<TResult extends DependencyBucket>(
    factory: DependencyFactoryFor<'controllers', TConfig, TResult>,
  ): DependencyManager<ReplaceBucket<TConfig, 'controllers', typeof factory>> {
    return this.controllers(factory);
  }

  eventHandlers<TResult extends DependencyBucket>(
    factory: DependencyFactoryFor<'eventHandlers', TConfig, TResult>,
  ): DependencyManager<ReplaceBucket<TConfig, 'eventHandlers', typeof factory>> {
    return this.withBucket('eventHandlers', factory);
  }

  init(): DependencyManager<TConfig, true> {
    const dependencies = createEmptyDependencies() as DependenciesSingleton;

    for (const key of dependenciesKeys) {
      const factory = this.#setup[key] ?? emptyFactory;
      dependencies[key] = factory(dependencies);
    }

    return new DependencyManager<TConfig, true>(
      this.#setup,
      dependencies as DependencyManagerDependencies<TConfig>,
    );
  }

  getDependencies(this: DependencyManager<TConfig, true>): DependencyManagerDependencies<TConfig> {
    if (!this.#dependencies) {
      throw new Error('DependencyManager must be initialized before accessing dependencies');
    }

    return this.#dependencies;
  }
}
